import React, { forwardRef } from 'react';
import MaterialButton from '@mui/material/Button';
import c from 'classnames';

import { renderNodeOrComponent } from '../../utils';
import {
  ALIGNMENT_CLASSES,
  BORDER_RADIUS,
  BUTTON_SIZE_CLASSES,
  COLOR_CLASSES,
  getIcons,
  PADDING_X,
  TEXT_COLOR_CLASSES,
  TYPOGRAPHY_CLASSES,
} from '../utils/utils';
import type { ButtonProps } from './types';

const Button = forwardRef<HTMLButtonElement, ButtonProps>((props, ref) => {
  const {
    alignment = 'center',
    appearance = 'primary',
    children,
    classes,
    className,
    disabled,
    endIcon,
    EndIcon,
    fullWidth,
    loading = false,
    onClick,
    shape = 'theme',
    size = 'medium',
    startIcon,
    StartIcon,
    typography,
    variant = 'filled',
    ...restProps
  } = props;

  const sizeClasses = BUTTON_SIZE_CLASSES[size];
  const borderRadius = BORDER_RADIUS[shape];
  const paddingX = PADDING_X[size][shape];
  const colorClasses = COLOR_CLASSES[variant][appearance];
  const textColorClasses = typography?.color || TEXT_COLOR_CLASSES[variant][appearance];
  const alignmentClasses = ALIGNMENT_CLASSES[alignment];
  const typographyVariant = typography?.variant || TYPOGRAPHY_CLASSES[size];
  const startIconRendered = renderNodeOrComponent({
    Component: StartIcon,
    node: startIcon,
    props: { className: classes?.icon, fillColorToken: classes?.fillColorToken },
  });
  const endIconRendered = renderNodeOrComponent({
    Component: EndIcon,
    node: endIcon,
    props: { className: classes?.icon, fillColorToken: classes?.fillColorToken },
  });
  const icons = getIcons({
    appearance,
    endIcon: endIconRendered,
    loading,
    startIcon: startIconRendered,
    variant,
  });

  return (
    <MaterialButton
      ref={ref}
      className={c(
        'normal-case tracking-normal py-0 inline-flex',
        sizeClasses,
        borderRadius,
        paddingX,
        colorClasses,
        textColorClasses,
        alignmentClasses,
        typographyVariant,
        className,
        typography?.className,
      )}
      classes={classes}
      disabled={disabled || loading}
      endIcon={icons.endIcon}
      fullWidth={fullWidth}
      startIcon={icons.startIcon}
      onClick={onClick}
      {...restProps}>
      {children}
    </MaterialButton>
  );
});

export default Button;
